import "typings";

import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AuthSelector, MarketConfigService } from "@obg/ngx.core";
import { PlatformService } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { includes, isEqual, isUndefined } from "lodash-es";
import { interval, of } from "rxjs";
import {
	concatMap,
	distinctUntilChanged,
	filter,
	map,
	mergeMap,
	switchMap,
	take,
	tap,
	withLatestFrom,
} from "rxjs/operators";

import { APP_LIFE_CYCLE_ACTION_TYPE } from "../../app-lifecycle/app-lifecycle.action";
import { CommonState } from "../../state";
import { DataProtectionConfigService } from "../data-protection.config";
import { DataProtectionSelector } from "../data-protection.selector";
import { MARKETING_SETTINGS_ACTION_TYPE } from "../marketing-settings/marketing-settings.action";
import { Consents } from "../marketing-settings/marketing-settings.model";

import { COOKIE_CONSENT_ACTION_TYPE, CookieConsentAction } from "./cookie-consent.action";
import { CookieConsentConfigService } from "./cookie-consent.config";
import { cookieConsentAccepted, cookieConsentDeclined, OneTrustConsentGroups } from "./cookie-consent.const";
import {
	ConsentCookie,
	ConsentType,
	GDPRConsent,
	GTMConsentStatus,
	UpdateConsentPayload,
	UserConsent,
} from "./cookie-consent.model";
import { CookieConsentSelector } from "./cookie-consent.selector";
import { CookieConsentService } from "./cookie-consent.service";

@Injectable()
export class CookieConsentEffect {
	// TODO [OneTrust]:
	// This file needs to be refactored once oneTrust is rolled out on all the brands: https://edgealpha.corpsson.com/browse/AOCC-3162
	setCookieConsent$ = createEffect(() =>
		this.actions$.pipe(
			ofType(APP_LIFE_CYCLE_ACTION_TYPE.initSuccess),
			map(() => this.service.readCookie()),
			map((consentCookie) => this.action.set(consentCookie && !!consentCookie.marketing))
		)
	);

	hideoneTrustBarForBackwardsCookieConsent$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(APP_LIFE_CYCLE_ACTION_TYPE.initSuccess),
				filter(() => !!this.cookieConsentConfigService.getOneTrustIsEnabled() && !!this.service.readCookie()),
				switchMap(() =>
					interval(100).pipe(
						filter(() => typeof OneTrust !== "undefined"),
						map(() => document.getElementById("onetrust-banner-sdk") as HTMLInputElement),
						filter((oneTrustBar) => !!oneTrustBar),
						take(1),
						map((oneTrustBar) => {
							// hide the one trust bar avoid flicking in the screen;
							oneTrustBar.hidden = true;
						})
					)
				)
			),
		{ dispatch: false }
	);

	setCookieConsentEnabled$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.resolveEnd),
			map(
				({
					payload: {
						current: { name },
					},
				}) => !includes(this.config.getCookieConsentDisabledRoutes(), name)
			),
			distinctUntilChanged(),
			mergeMap((isGdprEnabledRoute) =>
				this.store.pipe(this.dataProtectionSelector.isOneTrustEnabledForCurrentCountry$()).pipe(
					map((isGdprEnabledCountry) => {
						const marketCode = this.marketConfig.getLanguageCode();
						const isEnabled = this.consentConfig.getOneTrustIsEnabled();
						const cdnUrl = this.consentConfig.getOneTrustCdnUrl();
						const dataDomainScript = this.consentConfig.getOneTrustDataDomainScript();
						const dataDocumentLanguage = this.consentConfig.getOneTrustDataDocumentLanguage();
						const dataLanguage = this.consentConfig.getOneTrustDataLanguage(marketCode);
						if (isEnabled && cdnUrl && dataDomainScript && isGdprEnabledCountry) {
							this.service.injectOneTrust(cdnUrl, dataDomainScript, dataDocumentLanguage, dataLanguage);
						}
						return this.action.setEnabled(isGdprEnabledRoute && isGdprEnabledCountry && !isEnabled);
					})
				)
			)
		)
	);

	autoDeclineCookieConsentForRoute$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RouteAction.resolveEnd),
			filter(
				({
					payload: {
						current: { name },
					},
				}) =>
					!this.config.getCookieConsentDisabled() &&
					includes(this.config.getCookieConsentAutomaticDeclineRoutes(), name)
			),
			withLatestFrom(this.store.pipe(select(this.selector.isConsentGiven()))),
			filter(([, isConsentGiven]) => !isConsentGiven),
			map(() => this.action.update(cookieConsentDeclined, false))
		)
	);

	updateCookieConsent$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COOKIE_CONSENT_ACTION_TYPE.update),
			map((x: ActionWithPayload<UpdateConsentPayload>) => x.payload.consentProperties.consentCookie),
			tap((consent) => this.service.writeCookie(consent)),
			map((consent) => this.action.updateSuccess(consent))
		)
	);

	updateSuccessCookieConsent$ = createEffect(() =>
		this.actions$.pipe(
			ofType(COOKIE_CONSENT_ACTION_TYPE.updateSuccess),
			map((consent: ActionWithPayload<ConsentCookie>) =>
				this.action.set(consent.payload.targeting === ConsentType.Accepted)
			)
		)
	);

	updateCookieConsentOnUpdateConsents$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateConsents),
			map((x: ActionWithPayload<Partial<Consents>>) => x.payload),
			concatMap((action) => of(action).pipe(withLatestFrom(this.store.select(AuthSelector.isAuthenticated)))),
			filter(
				([consents, isAuthenticated]) =>
					!isAuthenticated &&
					!isUndefined(consents.cookie) &&
					!this.cookieConsentConfigService.getOneTrustIsEnabled()
			),
			map(([consents]) => this.getCookieConsentAction(consents))
		)
	);

	updateCookieConsentOnUpdateCustomerConsentsSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsentsSuccess),
			map((action: ActionWithPayload<Partial<Consents>>) => action.payload),
			filter(
				(consents) => !isUndefined(consents.cookie) && !this.cookieConsentConfigService.getOneTrustIsEnabled()
			),
			map((consents) => this.getCookieConsentAction(consents))
		)
	);

	setCookieConsentOnGettingConsents$ = createEffect(() =>
		this.actions$.pipe(
			ofType(MARKETING_SETTINGS_ACTION_TYPE.getConsentsSuccess),
			filter(() => !this.config.getCookieConsentDisabled()),
			map((action: ActionWithPayload<Consents>) => action.payload),
			withLatestFrom(
				this.store.pipe(select(this.selector.isConsentGiven())),
				this.store.pipe(select(this.selector.isAccepted()))
			),
			filter(
				([consents, isConsentGiven, isAccepted]) =>
					(!isConsentGiven || consents.cookie !== isAccepted) &&
					!this.cookieConsentConfigService.getOneTrustIsEnabled()
			),
			map(([consents]) => this.getCookieConsentAction(consents))
		)
	);

	handleOneTrustConsentChange$ = this.platformService.forClient(() =>
		createEffect(
			() =>
				this.actions$.pipe(
					ofType(APP_LIFE_CYCLE_ACTION_TYPE.initSuccess),
					filter(() => this.cookieConsentConfigService.getOneTrustIsEnabled()),
					switchMap(() =>
						interval(1000).pipe(
							filter(() => typeof OneTrust !== "undefined"),
							take(1),
							tap(() => {
								const consentCookie = this.service.readCookie();
								let prevOneTrustActiveGroup = OnetrustActiveGroups;
								let currentConsent = this.mapOneTrustProperties(prevOneTrustActiveGroup);
								let isBackwardCompatibilityEvent = this.isBackwardCompatible(consentCookie);

								OneTrust.OnConsentChanged((payload: CustomEvent) => {
									const updatedConsent = this.mapOneTrustProperties(
										payload.detail.toString(),
										prevOneTrustActiveGroup
									);

									if (!isEqual(currentConsent, updatedConsent) && !isBackwardCompatibilityEvent) {
										this.store.dispatch(this.cookieConsentAction.update(updatedConsent, true));
									} else {
										this.store.dispatch(this.cookieConsentAction.update(updatedConsent, false));
										isBackwardCompatibilityEvent = false;
									}

									currentConsent = updatedConsent;
									prevOneTrustActiveGroup = OnetrustActiveGroups;
									return;
								});

								if (
									!isBackwardCompatibilityEvent &&
									isEqual(consentCookie, currentConsent.consentCookie)
								) {
									return;
								}

								if (!consentCookie) {
									return;
								}

								const keys = Object.keys(consentCookie);
								if (keys.length === 1) {
									// backward compatibility scenario
									if (consentCookie?.marketing === ConsentType.Accepted) {
										OneTrust.AllowAll();
									} else if (consentCookie?.marketing === ConsentType.Declined) {
										OneTrust.RejectAll();
									}
								} else {
									// full copy scenario
									for (const key of keys) {
										switch (key) {
											// C0001 is enabled by default and not toggleable
											case "performance":
												OneTrust.UpdateConsent(
													"Category",
													consentCookie[key] ? "C0002:1" : "C0002:0"
												);
												break;
											case "functional":
												OneTrust.UpdateConsent(
													"Category",
													consentCookie[key] ? "C0003:1" : "C0003:0"
												);
												break;
											case "targeting":
												// targeting is the same as marketing
												OneTrust.UpdateConsent(
													"Category",
													consentCookie[key] ? "C0004:1" : "C0004:0"
												);
												break;
										}
									}
								}
							})
						)
					)
				),
			{ dispatch: false }
		)
	);

	constructor(
		private actions$: Actions,
		private service: CookieConsentService,
		private action: CookieConsentAction,
		private config: DataProtectionConfigService,
		private consentConfig: CookieConsentConfigService,
		private store: Store<CommonState>,
		private selector: CookieConsentSelector,
		private dataProtectionSelector: DataProtectionSelector,
		private cookieConsentAction: CookieConsentAction,
		private cookieConsentConfigService: CookieConsentConfigService,
		private platformService: PlatformService,
		private marketConfig: MarketConfigService
	) {}

	mapOneTrustProperties(oneTrustActiveGroup: string, prevOneTrustActiveGroup?: string): UserConsent {
		const consentCookie: ConsentCookie = {
			marketing: 0,
			functional: 0,
			performance: 0,
			targeting: 0,
		};

		const gdprConsent: GDPRConsent = {
			gdpr_functional_enabled: GTMConsentStatus.Declined,
			gdpr_targeting_enabled: GTMConsentStatus.Declined,
			gdpr_performance_enabled: GTMConsentStatus.Declined,
		};

		if (oneTrustActiveGroup) {
			const oneTrustFilteredGroup = oneTrustActiveGroup
				.split(",")
				.filter((consentProperty) => consentProperty.startsWith("C"));
			oneTrustFilteredGroup.map((oneTrustProperty) => {
				switch (oneTrustProperty) {
					case OneTrustConsentGroups.FunctionalCookies:
						if (
							prevOneTrustActiveGroup &&
							!prevOneTrustActiveGroup.includes(OneTrustConsentGroups.FunctionalCookies)
						) {
							gdprConsent.GDPRFunctionalAccepted = GTMConsentStatus.Accepted;
						}
						gdprConsent.gdpr_functional_enabled = GTMConsentStatus.Accepted;
						consentCookie.functional = 1;
						break;

					case OneTrustConsentGroups.PerformanceCookies:
						if (
							prevOneTrustActiveGroup &&
							!prevOneTrustActiveGroup.includes(OneTrustConsentGroups.PerformanceCookies)
						) {
							gdprConsent.GDPRPerformanceAccepted = GTMConsentStatus.Accepted;
						}
						gdprConsent.gdpr_performance_enabled = GTMConsentStatus.Accepted;
						consentCookie.performance = 1;
						break;

					case OneTrustConsentGroups.TargetingCookies:
						if (
							prevOneTrustActiveGroup &&
							!prevOneTrustActiveGroup.includes(OneTrustConsentGroups.TargetingCookies)
						) {
							gdprConsent.GDPRTargetingAccepted = GTMConsentStatus.Accepted;
						}
						gdprConsent.gdpr_targeting_enabled = GTMConsentStatus.Accepted;
						consentCookie.marketing = 1;
						consentCookie.targeting = 1;
						break;
				}
			});
		}
		return { consentCookie, gdprConsent };
	}

	private getCookieConsentAction(consents: Partial<Consents>) {
		const consent: UserConsent = consents.cookie ? cookieConsentAccepted : cookieConsentDeclined;
		return this.action.update(consent);
	}

	private isBackwardCompatible(consentCookie: ConsentCookie | undefined): boolean {
		return typeof consentCookie?.targeting === "undefined" && typeof consentCookie?.marketing !== "undefined";
	}
}
